import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, Ellipsis } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import type { CorStatus } from '../tokens/tokens';
import { MenuSuspenso, type ItemMenuSuspenso } from './MenuSuspenso';
import { EtiquetaStatus } from './Pilula';

export interface ColunaGrade<T> {
  campo: string;
  titulo: string;
  /** Largura CSS da coluna no grid (ex.: '120px', 'minmax(160px, 2fr)'). */
  largura?: string;
  alinhar?: 'esquerda' | 'direita';
  mono?: boolean;
  ordenavel?: boolean;
  formatar?: (valor: unknown, linha: T) => ReactNode;
  /** Segunda linha da célula, em texto secundário (ex.: código e nome fantasia abaixo da razão social). */
  secundario?: (linha: T) => ReactNode;
  negativo?: (valor: unknown) => boolean;
}

export interface OrdemGrade {
  campo: string;
  direcao: 'asc' | 'desc';
}

export interface GradeProps<T> {
  colunas: ColunaGrade<T>[];
  linhas: T[];
  /** Total real (aria-rowcount); linhas ainda não carregadas aparecem como esqueleto. */
  total: number;
  chave: (linha: T) => string | number;
  rotulo: string;
  carregando?: boolean;
  /** Pedido de mais linhas quando a rolagem se aproxima do fim do que já foi carregado. */
  aoPrecisarDeMais?: () => void;
  ordem?: OrdemGrade[];
  aoOrdenar?: (campo: string, adicionar: boolean) => void;
  legenda?: (linha: T) => { cor: CorStatus; rotulo: string } | null;
  selecionadas?: ReadonlySet<string | number>;
  aoSelecionar?: (chaves: Set<string | number>) => void;
  /** Enter ou duplo clique. */
  aoAbrir?: (linha: T) => void;
  /** Linha focada mudou (teclado ou clique) — painéis de inspeção seguem o foco sem abrir o registro. */
  aoFocar?: (linha: T) => void;
  /** Teclas que a página trata (Insert, F4, Delete…) com a linha focada. */
  aoTecla?: (e: KeyboardEvent<HTMLDivElement>, linha: T | undefined) => void;
  focoInicial?: string | number;
  destacar?: string | number;
  estadoVazio?: ReactNode;
  alturaLinha?: number;
  /** Título da coluna da legenda, mostrada como etiqueta no fim da linha. */
  tituloLegenda?: string;
  /** Ações da linha no menu "⋯" (abrir, alterar, copiar, excluir…), já filtradas por permissão pela página. */
  acoesLinha?: (linha: T) => ItemMenuSuspenso[];
}

function alturaDoToken(): number {
  if (typeof window === 'undefined') return 30;
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--altura-linha'));
  return Number.isFinite(v) && v > 0 ? v : 30;
}

const LARGURA_LEGENDA = 104;
const LARGURA_ACOES = 48;
const LARGURA_SELECAO = 36;

/**
 * Largura mínima de uma coluna em px: o valor fixo (`96px`) ou o mínimo do `minmax` (`minmax(140px, 1fr)`). Todas as linhas usam a soma
 * como largura mínima comum — com `max-content` cada linha media o próprio conteúdo e as frações se desalinhavam do cabeçalho.
 */
function minimoDaColuna(largura: string | undefined): number {
  const px = /(\d+(?:\.\d+)?)px/.exec(largura ?? 'minmax(140px, 1fr)');
  return px ? Number(px[1]) : 140;
}

export function Grade<T>(props: GradeProps<T>) {
  const { colunas, linhas, total, chave, rotulo, carregando, aoPrecisarDeMais, ordem = [], aoOrdenar, legenda, selecionadas, aoSelecionar, aoAbrir, aoTecla,
    focoInicial, destacar, estadoVazio, aoFocar, tituloLegenda = 'Situação', acoesLinha } = props;
  const id = useId();
  const rolagem = useRef<HTMLDivElement>(null);
  const [alturaLinha, setAlturaLinha] = useState(props.alturaLinha ?? 30);
  useLayoutEffect(() => {
    if (!props.alturaLinha) setAlturaLinha(alturaDoToken());
  }, [props.alturaLinha]);

  const [foco, setFocoEstado] = useState(0);
  const linhasTotais = Math.max(total, linhas.length);
  const virtual = useVirtualizer({ count: linhasTotais, getScrollElement: () => rolagem.current, estimateSize: () => alturaLinha, overscan: 6 });

  // Foco inicial numa linha conhecida (ex.: registro recém-gravado). Enquanto ela não chega (lista ainda com o cache anterior), o foco na linha 0
  // é provisório: Enter/F4/Delete não agem sobre outro registro. Mover com o teclado ou o mouse é escolha do usuário e encerra a espera.
  const [focoResolvido, setFocoResolvido] = useState<string | number | undefined>(undefined);
  const focoPendente = focoInicial !== undefined && focoResolvido !== focoInicial;
  const setFoco = useCallback((i: number) => {
    setFocoEstado(i);
    setFocoResolvido(focoInicial);
  }, [focoInicial]);
  // Layout, não efeito: aplicado na mesma entrega do render, antes de o navegador processar qualquer tecla (com useEffect, um Enter logo depois
  // da pintura abria a linha 0 — outro registro).
  useLayoutEffect(() => {
    if (!focoPendente) return;
    const i = linhas.findIndex((l) => chave(l) === focoInicial);
    if (i >= 0) {
      setFoco(i);
      virtual.scrollToIndex(i, { align: 'auto' });
    }
  }, [focoPendente, focoInicial, linhas, chave, virtual, setFoco]);

  const linhaFocada = linhas[foco];
  useEffect(() => {
    if (linhaFocada !== undefined) aoFocar?.(linhaFocada);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só quando a linha focada muda
  }, [linhaFocada]);

  const itens = virtual.getVirtualItems();
  const ultimo = itens.at(-1)?.index ?? 0;
  useEffect(() => {
    if (aoPrecisarDeMais && !carregando && linhas.length < total && ultimo >= linhas.length - 24) aoPrecisarDeMais();
  }, [ultimo, linhas.length, total, carregando, aoPrecisarDeMais]);

  const mover = useCallback((i: number) => {
    const alvo = Math.max(0, Math.min(linhasTotais - 1, i));
    setFoco(alvo);
    virtual.scrollToIndex(alvo, { align: 'auto' });
  }, [linhasTotais, virtual, setFoco]);

  const alternar = (k: string | number, estender = false) => {
    if (!aoSelecionar) return;
    const s = new Set(selecionadas ?? []);
    if (s.has(k) && !estender) s.delete(k);
    else s.add(k);
    aoSelecionar(s);
  };
  // Shift+clique marca do último ponto de ancoragem até a linha clicada (seleção por intervalo, como nas tabelas do Mac).
  const ancora = useRef<number | null>(null);
  const marcarIntervalo = (ate: number) => {
    if (!aoSelecionar) return;
    // Sem âncora (primeiro clique), o intervalo é só a linha clicada: não parte do foco inicial, que a pessoa não escolheu.
    const de = ancora.current ?? ate;
    const s = new Set(selecionadas ?? []);
    for (let i = Math.min(de, ate); i <= Math.max(de, ate); i++) {
      const l = linhas[i];
      if (l !== undefined) s.add(chave(l));
    }
    aoSelecionar(s);
  };
  const clicarLinha = (e: MouseEvent<HTMLDivElement>, i: number, k: string | number | undefined) => {
    if (aoSelecionar && k !== undefined && e.shiftKey) {
      e.preventDefault();
      marcarIntervalo(i);
      if (ancora.current === null) ancora.current = i;
    } else if (aoSelecionar && k !== undefined && (e.ctrlKey || e.metaKey)) {
      alternar(k);
      ancora.current = i;
    } else {
      ancora.current = i;
    }
    setFoco(i);
  };
  // A grade virtual pode ter buracos (blocos ainda não carregados): só conta e marca as linhas que já chegaram.
  const chavesCarregadas = linhas.flatMap((l) => (l === undefined ? [] : [chave(l)]));
  const marcadas = selecionadas?.size ?? 0;
  const todasMarcadas = chavesCarregadas.length > 0 && chavesCarregadas.every((k) => selecionadas?.has(k));

  const teclado = (e: KeyboardEvent<HTMLDivElement>) => {
    const pagina = Math.max(1, Math.floor((rolagem.current?.clientHeight ?? 300) / alturaLinha) - 1);
    const linha = linhas[foco];
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (e.shiftKey && linha) alternar(chave(linha), true);
        mover(foco + 1);
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (e.shiftKey && linha) alternar(chave(linha), true);
        mover(foco - 1);
        return;
      case 'PageDown':
        e.preventDefault();
        mover(foco + pagina);
        return;
      case 'PageUp':
        e.preventDefault();
        mover(foco - pagina);
        return;
      case 'Home':
        e.preventDefault();
        mover(0);
        return;
      case 'End':
        e.preventDefault();
        mover(linhasTotais - 1);
        return;
      case ' ':
        if (linha && aoSelecionar) {
          e.preventDefault();
          alternar(chave(linha));
        }
        return;
      case 'Enter':
        if (focoPendente) {
          e.preventDefault();
          return;
        }
        if (linha && aoAbrir) {
          e.preventDefault();
          aoAbrir(linha);
        }
        return;
      case 'a':
        if ((e.ctrlKey || e.metaKey) && aoSelecionar) {
          e.preventDefault();
          aoSelecionar(new Set(chavesCarregadas));
        }
        return;
    }
    // Ações da página sobre a linha (F4 alterar, Delete excluir…) também esperam a linha pedida.
    aoTecla?.(e, focoPendente ? undefined : linha);
  };

  const comSelecao = !!aoSelecionar;
  const template = [comSelecao ? `${LARGURA_SELECAO}px` : null, ...colunas.map((c) => c.largura ?? 'minmax(140px, 1fr)'), legenda ? `${LARGURA_LEGENDA}px` : null,
    acoesLinha ? `${LARGURA_ACOES}px` : null].filter(Boolean).join(' ');
  const larguraMinima = (comSelecao ? LARGURA_SELECAO : 0) + (legenda ? LARGURA_LEGENDA : 0) + (acoesLinha ? LARGURA_ACOES : 0)
    + colunas.reduce((s, c) => s + minimoDaColuna(c.largura), 0);
  const idLinha = (i: number) => `${id}-l${i}`;
  const ordemDe = (campo: string) => ordem.find((o) => o.campo === campo);

  if (!carregando && total === 0 && linhas.length === 0 && estadoVazio) return <div className="grade painel">{estadoVazio}</div>;

  return (
    <div className="grade painel" style={{ ['--_colunas' as string]: template, ['--_largura' as string]: `${larguraMinima}px`, ['--_largura-acoes' as string]: `${acoesLinha ? LARGURA_ACOES : 0}px` }}>
      <div
        ref={rolagem}
        className="grade-rolagem"
        role="grid"
        aria-label={rotulo}
        aria-rowcount={linhasTotais + 1}
        aria-colcount={colunas.length + (legenda ? 1 : 0) + (comSelecao ? 1 : 0) + (acoesLinha ? 1 : 0)}
        aria-multiselectable={aoSelecionar ? true : undefined}
        aria-activedescendant={linhasTotais > 0 ? idLinha(foco) : undefined}
        aria-busy={carregando || undefined}
        tabIndex={0}
        onKeyDown={teclado}
        data-testid="grade"
      >
        <div className="grade-cabecalho" role="row" aria-rowindex={1}>
          {comSelecao && (
            <span role="columnheader" className="grade-selecao" aria-label="Seleção">
              <input type="checkbox" tabIndex={-1} aria-label={todasMarcadas ? 'Desmarcar todas' : 'Marcar todas as linhas carregadas'}
                checked={todasMarcadas} ref={(el) => { if (el) el.indeterminate = marcadas > 0 && !todasMarcadas; }}
                onChange={() => aoSelecionar?.(todasMarcadas ? new Set() : new Set(chavesCarregadas))} />
            </span>
          )}
          {colunas.map((c) => {
            const o = ordemDe(c.campo);
            return (
              <span key={c.campo} role="columnheader" data-alinhar={c.alinhar} aria-sort={o ? (o.direcao === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" tabIndex={-1} title={c.titulo} onClick={(e) => c.ordenavel !== false && aoOrdenar?.(c.campo, e.shiftKey)}>
                  <span className="grade-titulo">{c.titulo}</span>
                  {o && (o.direcao === 'asc' ? <ArrowUp size={12} aria-hidden="true" /> : <ArrowDown size={12} aria-hidden="true" />)}
                </button>
              </span>
            );
          })}
          {legenda && <span role="columnheader" className="grade-cabecalho-legenda">{tituloLegenda}</span>}
          {acoesLinha && <span role="columnheader" aria-label="Ações" className="grade-cabecalho-acoes" />}
        </div>
        <div style={{ height: virtual.getTotalSize(), position: 'relative' }}>
          {itens.map((v) => {
            const linha = linhas[v.index];
            // Linhas recicladas: a chave é a posição na janela (índice módulo o tamanho dela, único numa janela contígua). Rolando, o React
            // atualiza o conteúdo das mesmas linhas em vez de desmontar e montar a janela inteira a cada quadro (P4, 07 §11.3).
            const vaga = v.index % itens.length;
            const k = linha === undefined ? undefined : chave(linha);
            const status = linha && legenda ? legenda(linha) : null;
            return (
              <div
                key={vaga}
                id={idLinha(v.index)}
                role="row"
                aria-rowindex={v.index + 2}
                aria-selected={aoSelecionar ? (k !== undefined && (selecionadas?.has(k) ?? false)) : undefined}
                className="grade-linha"
                data-par={v.index % 2 === 1 ? 'true' : undefined}
                data-focada={v.index === foco ? 'true' : undefined}
                data-nova={k !== undefined && k === destacar ? 'true' : undefined}
                style={{ transform: `translateY(${v.start}px)`, height: alturaLinha }}
                onClick={(e) => clicarLinha(e, v.index, k)}
                onDoubleClick={() => linha && aoAbrir?.(linha)}
              >
                {comSelecao && (
                  <span role="gridcell" className="grade-celula grade-selecao">
                    {k !== undefined && (
                      <input type="checkbox" tabIndex={-1} aria-label="Marcar linha" checked={selecionadas?.has(k) ?? false}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => { alternar(k); ancora.current = v.index; setFoco(v.index); }} />
                    )}
                  </span>
                )}
                {colunas.map((c) => {
                  if (linha === undefined) {
                    return <span key={c.campo} role="gridcell" className="grade-celula"><span className="grade-esqueleto" style={{ display: 'block', width: '60%' }} /></span>;
                  }
                  const valor = (linha as Record<string, unknown>)[c.campo];
                  const texto = c.formatar ? c.formatar(valor, linha) : valor === null || valor === undefined ? '' : String(valor);
                  const segunda = c.secundario?.(linha);
                  return (
                    <span key={c.campo} role="gridcell" className={c.mono ? 'grade-celula mono' : 'grade-celula'} data-alinhar={c.alinhar}
                      data-negativo={c.negativo?.(valor) ? 'true' : undefined} data-duas-linhas={c.secundario ? 'true' : undefined}>
                      {c.secundario ? <><span className="grade-celula-principal">{texto}</span>{segunda && <span className="grade-celula-secundaria">{segunda}</span>}</> : texto}
                    </span>
                  );
                })}
                {legenda && (
                  <span role="gridcell" className="grade-celula grade-legenda">{status && <EtiquetaStatus cor={status.cor} rotulo={status.rotulo} />}</span>
                )}
                {acoesLinha && (
                  <span role="gridcell" className="grade-celula grade-acoes" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                    {linha !== undefined && (
                      <MenuSuspenso rotulo="Ações da linha" alinhar="end" itens={acoesLinha(linha)}
                        gatilho={<button type="button" className="grade-acoes-botao" tabIndex={-1} aria-label="Ações da linha"><Ellipsis size={17} aria-hidden="true" /></button>} />
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
